import type {
	AdminPost,
	ChannelPublication,
	ChannelPublicationStatus,
	Platform,
	Surface,
} from "../model/types"

export const PLATFORMS: Platform[] = ["telegram", "tma", "mobile"]

export const PLATFORM_SURFACE: Record<Exclude<Platform, "telegram">, Surface> = {
	tma: "TMA",
	mobile: "MOBILE",
}

/** Короткое имя для колонок и фильтров. */
export const PLATFORM_LABEL: Record<Platform, string> = {
	telegram: "Канал",
	tma: "Мини-апп",
	mobile: "Мобильное",
}

/** Полное имя для карточек публикации. */
export const PLATFORM_TITLE: Record<Platform, string> = {
	telegram: "Telegram-канал",
	tma: "Мини-апп в Telegram",
	mobile: "Мобильное приложение",
}

export type PlacementState = "live" | "scheduled" | "failed" | "off"

export interface Placement {
	platform: Platform
	state: PlacementState
	/** Когда вышел, когда выйдет или когда сломалось. */
	at: string | null
	error: string | null
	publication: ChannelPublication | null
}

export const PLACEMENT_LABEL: Record<PlacementState, string> = {
	live: "Опубликован",
	scheduled: "Запланирован",
	failed: "Ошибка",
	off: "Не опубликован",
}

const CHANNEL_PRIORITY: ChannelPublicationStatus[] = [
	"PUBLISHED",
	"SCHEDULED",
	"FAILED",
	"DRAFT",
	"REMOVED",
]

/** Публикация, которая сейчас говорит о состоянии поста в канале. */
export function currentChannelPublication(post: AdminPost): ChannelPublication | null {
	const byPriority = [...post.publications].sort(
		(left, right) =>
			CHANNEL_PRIORITY.indexOf(left.status) - CHANNEL_PRIORITY.indexOf(right.status),
	)
	return byPriority[0] ?? null
}

function channelPlacement(post: AdminPost): Placement {
	const publication = currentChannelPublication(post)
	const base = { platform: "telegram" as const, publication, error: null }
	switch (publication?.status) {
		case "PUBLISHED":
			return { ...base, state: "live", at: publication.publishedAt ?? publication.scheduledAt }
		case "SCHEDULED":
			return { ...base, state: "scheduled", at: publication.scheduledAt }
		case "FAILED":
			return {
				...base,
				state: "failed",
				at: publication.failedAt,
				error: publication.errorMessage,
			}
		default:
			return { ...base, state: "off", at: null }
	}
}

function surfacePlacement(post: AdminPost, platform: "tma" | "mobile"): Placement {
	const surface = post.surfaces[PLATFORM_SURFACE[platform]]
	const base = { platform, publication: null, at: surface?.updatedAt ?? null }
	if (surface?.status === "VISIBLE") return { ...base, state: "live", error: null }
	if (surface?.status === "FAILED") return { ...base, state: "failed", error: surface.note }
	return { ...base, state: "off", at: null, error: null }
}

export function placementOf(post: AdminPost, platform: Platform): Placement {
	return platform === "telegram" ? channelPlacement(post) : surfacePlacement(post, platform)
}

/** Черновик: материала нет ни на одной площадке. */
export function isDraft(post: AdminPost): boolean {
	return PLATFORMS.every((platform) => placementOf(post, platform).state === "off")
}
